import styles from './TargetGroupTable.module.css';
import CandidateScoresTable from './CandidateScoresTable';
import { useGetParticipantActivityQuery } from '../../../store/api/participants';
import { Participant } from '../../../typings/participant';
import Box from '@material-ui/core/Box';
import { Spinner } from 'evergreen-ui';
import React from 'react';
import {
    isCultureFitCandidateScore,
    isInterviewCandidateScore,
    isMentalTestCandidateScore,
} from '../utils';
import { Assessment } from '../../../typings/assessment';
import { findLatestError } from '../../../utils/ErrorHandling/findLatestError';
import { useGetScanTemplateQuery } from '../../../store/api/scanTemplates';

interface CandidateActivityRowProps {
    companyId: string;
    groupId: string;
    participant: Participant;
    invitationId: string;
    scanTemplateId: string;
    scanTemplate?: Assessment;
    isSuperAdmin: boolean;
    showInfo: boolean;
    groupHasOptOutQuestionnaire: boolean;
}

const CandidateActivityRow = (props: CandidateActivityRowProps) => {
    const {
        isSuperAdmin,
        companyId,
        groupId,
        participant,
        scanTemplate,
        showInfo,
        scanTemplateId,
        groupHasOptOutQuestionnaire,
    } = props;

    const { data: fetchedScanTemplate } = useGetScanTemplateQuery(
        {
            companyIdentifier: companyId,
            scanTemplateId: scanTemplateId,
        },
        {
            skip: !showInfo || !!scanTemplate,
        },
    );

    const scan = scanTemplate || fetchedScanTemplate;

    const {
        data: participantActivity,
        isError,
        isFetching,
    } = useGetParticipantActivityQuery(
        {
            companyIdentifier: companyId,
            groupId: groupId,
            userId: participant.user_id,
            includeReportUrl: false,
        },
        {
            skip: !showInfo,
        },
    );

    const isOptedOut =
        !!participantActivity?.invitation.preferences
            ?.video_recording_opted_out;

    const cultureFitScore = participantActivity?.scores?.find(
        isCultureFitCandidateScore,
    );
    const cultureFitEngagementScore =
        cultureFitScore?.data.culture_fit.company_engagement;
    const cultureFitWorkPreferenceScore =
        cultureFitScore?.data.culture_fit.categories;

    const interviewScore = participantActivity?.scores?.find(
        isInterviewCandidateScore,
    );
    const interviewConfidenceScore = interviewScore?.data.interview.confidence;
    const interviewMotivationScore =
        interviewScore?.data.interview.work_motivation;

    const mentalTestScore = participantActivity?.scores?.find(
        isMentalTestCandidateScore,
    );
    const mentalTestCategoriesScore =
        mentalTestScore?.data.mental_test.categories;
    const mentalTestEaseScore = mentalTestScore?.data.mental_test.ease;
    const mentalTestStressScore =
        mentalTestScore?.data.mental_test.stress_resilience;

    const hasCultureFitEngagementModule = !!scan?.modules?.find((module) => {
        return module.id === 'culture-fit';
    });
    const hasCultureFitWorkPreferencesModule = !!scan?.modules?.find(
        (module) => {
            return module.id === 'culture-fit-questionnaire';
        },
    );
    const hasSpeakingModule = !!scan?.modules?.find((module) => {
        return module.id === 'interview';
    });
    const hasCognitiveAbilitiesModule = !!scan?.modules?.find((module) => {
        return module.id === 'mental-test';
    });

    const areAllScanModulesScored = () => {
        return (
            !!cultureFitEngagementScore === hasCultureFitEngagementModule &&
            !!cultureFitWorkPreferenceScore ===
                hasCultureFitWorkPreferencesModule &&
            !!interviewConfidenceScore === hasSpeakingModule &&
            !!interviewMotivationScore === hasSpeakingModule &&
            !!mentalTestCategoriesScore === hasCognitiveAbilitiesModule &&
            !!mentalTestEaseScore === hasCognitiveAbilitiesModule &&
            !!mentalTestStressScore === hasCognitiveAbilitiesModule
        );
    };
    const areAllModulesScored = areAllScanModulesScored();

    const analysisStatus = participantActivity?.analysis?.status;
    const hasErrorStatus =
        participantActivity?.invitation?.status &&
        Object.keys(participantActivity.invitation.status).length;

    const latestErrorMessage = findLatestError(
        participantActivity?.invitation.status,
    );

    const mailSubject = `Analysis failed for user with invitation= ${participant.invitation_id}`;
    const encodedSubject = encodeURIComponent(mailSubject);

    return (
        <>
            {!isFetching && !isError && (
                <>
                    {!!participantActivity?.report && areAllModulesScored && (
                        <div className={styles.result_notification__green}>
                            The analysis was successful. Results are available
                        </div>
                    )}
                    {!!participantActivity?.report && !areAllModulesScored && (
                        <div className={styles.result_notification__green}>
                            The analysis was partially successful. Results are
                            available
                        </div>
                    )}
                    {!participantActivity?.report &&
                        !hasErrorStatus &&
                        analysisStatus !== 'pending' && (
                            <div className={styles.result_notification__gray}>
                                The scan has not started yet.
                            </div>
                        )}
                    {!participantActivity?.report &&
                        !hasErrorStatus &&
                        analysisStatus === 'pending' && (
                            <div className={styles.result_notification__blue}>
                                Analysis in progress. The report will be ready
                                soon.
                            </div>
                        )}
                    {!participantActivity?.report &&
                        hasErrorStatus &&
                        analysisStatus === 'pending' && (
                            <div className={styles.result_notification__oranje}>
                                <p>
                                    {!!latestErrorMessage && isSuperAdmin
                                        ? latestErrorMessage.adminMessage
                                        : !!latestErrorMessage && !isSuperAdmin
                                        ? latestErrorMessage.clientMessage
                                        : 'Something went wrong in the analysis. Please reach out to support. Apologies for the inconvenience.'}
                                </p>
                                <p>
                                    <a
                                        href={`mailto:support@neurolytics.ai?subject=${encodedSubject}`}>
                                        Contact support
                                    </a>
                                </p>
                            </div>
                        )}
                    <Box>
                        <div className={styles.user_activity}>
                            <CandidateScoresTable
                                isOptedOut={isOptedOut}
                                groupHasOptOutQuestionnaire={
                                    groupHasOptOutQuestionnaire
                                }
                                scores={participantActivity?.scores}
                                hasCultureFitEngagementModule={
                                    hasCultureFitEngagementModule
                                }
                                hasCultureFitWorkPreferencesModule={
                                    hasCultureFitWorkPreferencesModule
                                }
                                hasSpeakingModule={hasSpeakingModule}
                                hasCognitiveAbilitiesModule={
                                    hasCognitiveAbilitiesModule
                                }
                            />
                        </div>
                    </Box>
                </>
            )}

            {!isFetching && isError && (
                <Box>
                    <div className={styles.user_activity__loading}>
                        <span>
                            Couldn't find activity information. Please reach out
                            to support.
                        </span>
                    </div>
                </Box>
            )}

            {isFetching && (
                <Box>
                    <div className={styles.user_activity__loading}>
                        <Spinner size={22} />
                        <span>Loading...</span>
                    </div>
                </Box>
            )}
        </>
    );
};

export default CandidateActivityRow;
